import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { skillsApi, categoriesApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import StarButton from '../components/StarButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  ArrowLeft, Eye, ChevronRight,
  PenTool, Briefcase, Megaphone, GraduationCap, Palette, Workflow,
  Bot, Code, BarChart3, FileText, BookOpen, Globe, 
  Shield, Server, Microscope, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategoryData();
  }, [slug]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, skillsRes] = await Promise.all([
        categoriesApi.list(),
        skillsApi.list({ category_id: slug }),
      ]);
      const foundCategory = categoriesRes.data.find(c => c.slug === slug);
      setCategory(foundCategory);
      setSkills(skillsRes.data);
    } catch (error) {
      console.error('Failed to load category', error);
      toast.error('Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (slug) => {
    const iconMap = {
      'writing-content': PenTool,
      'business-productivity': Briefcase,
      'marketing-sales': Megaphone,
      'education-learning': GraduationCap,
      'creative-design': Palette,
      'skills-workflows': Workflow,
      'agents-ai': Bot,
      'developer-tools': Code,
      'data-analytics': BarChart3,
      'documents-files': FileText,
      'research-knowledge': BookOpen,
      'web-apps': Globe,
      'security-compliance': Shield,
      'devops-infrastructure': Server,
      'science-research': Microscope,
      'custom-experimental': Zap,
    };
    return iconMap[slug] || Code;
  };

  const IconComponent = category ? getCategoryIcon(category.slug) : Code;

  return (
    <div className="space-y-8">
      {/* Back Button & Breadcrumbs */}
      <div>
        <button
          onClick={() => navigate('/browse')}
          className="flex items-center gap-2 text-text-gray dark:text-dark-text-secondary hover:text-text-dark dark:hover:text-dark-text mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Browse
        </button>
        
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-text-gray dark:text-dark-text-secondary mb-6">
          <Link to="/browse" className="hover:text-primary transition-colors">Browse</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-text-dark dark:text-dark-text font-medium">{category?.name || 'Category'}</span>
        </div>
      </div>

      {/* Category Header */}
      {category && (
        <GlassCard variant="strong" className="p-8">
          <div className="flex items-start gap-6">
            <div className="p-4 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-2xl">
              <IconComponent className="h-12 w-12 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-3">
                {category.name}
              </h1>
              <p className="text-lg text-text-gray dark:text-dark-text-secondary mb-4">
                {category.description}
              </p>
              <Badge className="bg-primary/10 text-primary border-0">
                {skills.length} {skills.length === 1 ? 'skill' : 'skills'}
              </Badge>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Skills Grid */}
      <div>
        <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text mb-6">
          Skills in this Category
        </h2>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <GlassCard key={i} className="h-64 skeleton"></GlassCard>
            ))}
          </div>
        ) : skills.length === 0 ? (
          <GlassCard variant="strong" className="text-center py-16">
            <IconComponent className="h-16 w-16 mx-auto mb-4 text-text-light dark:text-dark-text-secondary opacity-50" />
            <h3 className="text-xl font-semibold text-text-dark dark:text-dark-text mb-2">No skills yet</h3>
            <p className="text-text-gray dark:text-dark-text-secondary mb-6">Be the first to create a skill in this category!</p>
            <Button
              onClick={() => navigate('/builder')}
              className="gradient-primary text-white rounded-xl"
            >
              Create Skill
            </Button>
          </GlassCard>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill) => (
              <Link key={skill.id} to={`/skills/${skill.id}`}>
                <GlassCard hover className="p-6 h-full">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text pr-2">
                      {skill.name}
                    </h3>
                    <StarButton 
                      skillId={skill.id} 
                      starCount={skill.star_count || 0}
                      size="small"
                      showCount={false}
                    />
                  </div>
                  
                  <p className="text-sm text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-4">
                    {skill.description || 'No description'}
                  </p>
                  
                  {/* Creator */}
                  <div className="flex items-center gap-2 mb-4">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={skill.creator?.avatar_url} />
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {skill.creator?.username?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-text-gray dark:text-dark-text-secondary">
                      {skill.creator?.username || 'Unknown'}
                    </span>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-text-gray dark:text-dark-text-secondary">
                    <div className="flex items-center gap-1">
                      <span className="font-medium">{skill.star_count || 0}</span>
                      <span>stars</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{skill.view_count || 0}</span>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;